// src/components/PollResults.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Paper,
    Typography,
    Box,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Stack,
} from "@mui/material";
import {
    CheckCircle,
    Cancel,
    RestaurantMenu,
    NoMeals,
} from "@mui/icons-material";
import { PollResponse } from "../../types";
import { STORAGE_KEYS } from "../../utils/StorageKeys";
import { GetPoll, GetPollPayload } from "../../utils/APIRequest";
// import { PollResponse } from "../types";
// import { GetPoll, GetPollPayload } from "../utils/APIRequest";
// import { formatDate } from "../utils/CommnMethods";
// import { STORAGE_KEYS } from "../utils/StorageKeys";


interface StatsType {
    total: number;
    attending: number;
    notAttending: number;
    vegCount: number;
    nonVegCount: number;
    attendingPercentage: number;
    vegPercentage: number;
    nonVegPercentage: number;
}
interface PollResultsProps {
    response: PollResponse[];
}

const PollResults: React.FC<PollResultsProps> = ({ response }) => {

    const [pollResponses, setPollResponses] = useState<PollResponse[]>(response);
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

    const fetchPollResponses = async () => {
        // Simulate fetching poll responses from an API
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const pollDate = tomorrow.toISOString().split("T")[0];

        const pollPayload: GetPollPayload = {
            date: pollDate,
        }
        const data = await GetPoll(pollPayload);
        setPollResponses(data);
    };

    const stats: StatsType = useMemo(() => {
        const total = pollResponses.length;
        const attending = pollResponses.filter((r) => r.foodChoice !== "No").length;
        const notAttending = total - attending;
        const vegCount = pollResponses.filter((r) => r.foodChoice === "Veg").length;
        const nonVegCount = pollResponses.filter(
            (r) => r.foodChoice === "Non-Veg"
        ).length;

        return {
            total,
            attending,
            notAttending,
            vegCount,
            nonVegCount,
            attendingPercentage: total ? Math.round((attending / total) * 100) : 0,
            vegPercentage: attending ? Math.round((vegCount / attending) * 100) : 0,
            nonVegPercentage: attending
                ? Math.round((nonVegCount / attending) * 100)
                : 0,
        };
    }, [pollResponses]);

    useEffect(() => {
        setPollResponses(response);
    }, [response]);

    useEffect(() => {
        const interval = setInterval(() => {
            fetchPollResponses();
        }, 60000); // Fetch every 60 seconds

        return () => clearInterval(interval);
    }, []);

    if (!accessToken) {
        return (null);
    }

    if (pollResponses.length === 0) {
        return (
            <Paper elevation={3} sx={{ p: 3 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                    Poll Results
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}
                >
                    No responses yet. Be the first to respond!
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom>
                Poll Results
            </Typography>

            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Summary
                </Typography>

                {/* Replacing Grid with Stack */}
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{ mb: 2 }}
                >
                    <Box
                        sx={{
                            textAlign: "center",
                            p: 2,
                            border: "1px solid #eee",
                            borderRadius: 1,
                            flex: 1,
                        }}
                    >
                        <Typography variant="h4">{stats.total}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Total Responses
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            textAlign: "center",
                            p: 2,
                            border: "1px solid #eee",
                            borderRadius: 1,
                            bgcolor: "primary.light",
                            color: "white",
                            flex: 1,
                        }}
                    >
                        <Typography variant="h4">{stats.attending}</Typography>
                        <Typography variant="body2">
                            Attending ({stats.attendingPercentage}%)
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            textAlign: "center",
                            p: 2,
                            border: "1px solid #eee",
                            borderRadius: 1,
                            flex: 1,
                        }}
                    >
                        <Typography variant="h4">{stats.notAttending}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            Not Attending
                        </Typography>
                    </Box>
                </Stack>

                {stats.attending > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
                        <Chip
                            icon={<RestaurantMenu />}
                            label={`Veg: ${stats.vegCount} (${stats.vegPercentage}%)`}
                            color="secondary"
                            variant="outlined"
                        />
                        <Chip
                            icon={<NoMeals />}
                            label={`Non-Veg: ${stats.nonVegCount} (${stats.nonVegPercentage}%)`}
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                )}
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
                Individual Responses
            </Typography>

            <List>
                {pollResponses.map((response) => (
                    <ListItem key={response._id} divider>
                        <ListItemIcon>
                            {response.foodChoice !== "No" ? (
                                <CheckCircle color="success" />
                            ) : (
                                <Cancel color="error" />
                            )}
                        </ListItemIcon>
                        <ListItemText
                            primary={response.emp_name}
                            secondary={
                                response.foodChoice !== "No"
                                    ? `Attending - ${response.foodChoice === "Veg"
                                        ? "Vegetarian"
                                        : "Non-Vegetarian"
                                    }`
                                    : "Not Attending"
                            }
                        />
                        {response.foodChoice !== "No" && (
                            <Box>
                                {response.foodChoice === "Veg" ? (
                                    <RestaurantMenu color="secondary" />
                                ) : (
                                    <NoMeals color="primary" />
                                )}
                            </Box>
                        )}
                    </ListItem>
                ))}
            </List>
        </Paper>
    );
};

export default PollResults;